import React, { useState, useEffect } from "react";
import "./LoginForm.scss";
import AuthService from "../services/authService";
import { useMutation } from "react-query";
import { ERROR_CODES } from "../constants";
import { useTranslation } from "react-i18next";

interface SignupFormProps {
  onSubmit: (username: string, password: string) => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSubmit }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAgreePolicy, setAgreePolicy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const { t } = useTranslation();

  useEffect(() => {
    setIsButtonDisabled(
      username === "" ||
        email === "" ||
        password === "" ||
        confirmPassword === "" ||
        password.length < 6 ||
        !isAgreePolicy ||
        password !== confirmPassword,
    );
  }, [username, password, email, isAgreePolicy, confirmPassword]);

  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setConfirmPasswordError(t("Passwords do not match"));
    } else {
      setConfirmPasswordError(null);
    }
  }, [password, confirmPassword, t]);

  const mutation = useMutation(AuthService.signup, {
    onSuccess: (data) => {
      console.log(data);
      onSubmit(username, password);
    },
    onError: (error: any) => {
      console.log(error);
      if (error.code === ERROR_CODES.USER_EXISTS) {
        setError(t("The account is already exist"));
      } else if (error.code === ERROR_CODES.EMAIL_EXISTS) {
        setError(t("The email is already exist"));
      } else {
        setError(t("Something went wrong"));
      }
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const username = (event.target as any).username.value;
    const email = (event.target as any).email.value;
    const password = (event.target as any).password.value;
    const confirmPassword = (event.target as any).confirmPassword.value;
    if (password !== confirmPassword) {
      setConfirmPasswordError(t("Passwords do not match"));
      return;
    }
    mutation.mutate({ username, email, password });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8 w-full">
      <h3 className="title-popup mb-8 font-semibold leading-[2.5537rem]">
        {t("Create an account")}
      </h3>
      <input
        type="text"
        id="username"
        name="username"
        placeholder={t("Username")}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        className="mb-[1.25rem] h-[3.125rem] w-full rounded-[0.625rem] border p-2"
      />
      <input
        type="text"
        id="email"
        name="email"
        placeholder={t("Email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mb-[1.25rem] h-[3.125rem] w-full rounded-[0.625rem] border p-2"
      />
      <div className="relative mb-[1.25rem] w-full">
        <input
          type={showPassword ? "text" : "password"}
          id="password"
          name="password"
          placeholder={t("Password")}
          className="h-[3.125rem] w-full rounded-[0.625rem] border p-2 text-[0.875rem] leading-[1.1919rem]"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
          tabIndex={-1}
        >
          <img
            src={`../assets/images/eye${showPassword ? "-slash" : ""}.svg`}
            alt=""
          />
        </button>
      </div>
      <div className="relative mb-[1.25rem] w-full">
        <input
          type={showConfirmPassword ? "text" : "password"}
          id="confirmPassword"
          name="confirmPassword"
          placeholder={t("Confirm Password")}
          className="h-[3.125rem] w-full rounded-[0.625rem] border p-2 text-[0.875rem] leading-[1.1919rem]"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <button
          type="button"
          onClick={toggleConfirmPasswordVisibility}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
          tabIndex={-1}
        >
          <img
            src={`../assets/images/eye${showConfirmPassword ? "-slash" : ""}.svg`}
            alt=""
          />
        </button>
      </div>
      <div className="mt-2 text-[0.75rem] text-[#5E6D77]">
        {t("Minimum 6 characters")}
      </div>
      {confirmPasswordError && (
        <div className="mt-2 text-[0.75rem] text-[#DA3838]">
          {confirmPasswordError}
        </div>
      )}
      {error && (
        <div className="mt-2 text-[0.75rem] text-[#DA3838]">{error}</div>
      )}
      <button
        type="submit"
        disabled={isButtonDisabled}
        className={`text-1 mb-6 mt-6 h-[3.125rem] w-full rounded-full p-2 font-semibold text-white ${
          isButtonDisabled ? "bg-[#BFC5C9]" : "bg-[#232323]"
        }`}
      >
        {t("Register")}
      </button>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="rememberMe"
            name="rememberMe"
            className="custom-checkbox"
            checked={isAgreePolicy}
            onChange={() => setAgreePolicy(!isAgreePolicy)}
          />
          <label
            htmlFor="rememberMe"
            className="ml-2 block text-[0.875rem] font-normal text-[#5E6D77]"
          >
            {t("I confirm that I have read and accepted the ")}
            <button className="text-primary"> {t("privacy policy")} </button>
          </label>
        </div>
      </div>
    </form>
  );
};

export default SignupForm;
